import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { IFormData } from "../Forms/formFields";
import { Feature } from "ol";
import { ISummaryParams } from "../Registration/SummarySection";
import { useAppContext } from "./AppContext";
import { IEsriCodedValue } from "../Map/rest";
import { config } from "../Map/config";
import { IFullscreenViewOverlay } from "../Registration/Registration";
import { ApplicantType, DrawType, PageId } from "../Map/enums";
import { generateOverview, generateSnapshot } from "../Map/snapshot";
import { getDrawingSymbol, getOverviewMapSymbol } from "../Map/symbols";

export interface IRegistrationContext {
    userConsent: boolean;
    setUserConsent: Dispatch<SetStateAction<boolean>>;
    validateForm: string;
    setValidateForm: (timeStamp: string) => void;
    resetForm: number;
    setResetForm: Dispatch<SetStateAction<number>>;
    rawData: IFormData;
    setRawData: Dispatch<SetStateAction<IFormData>>;
    formFeature: Feature;
    setFormFeature: Dispatch<SetStateAction<Feature>>;
    attachments: FileList;
    setAttachments: Dispatch<SetStateAction<FileList>>;
    attachedProcurations: FileList;
    setAttachedProcurations: Dispatch<SetStateAction<FileList>>;
    summaryData: ISummaryParams;
    setSummaryData: Dispatch<SetStateAction<ISummaryParams>>;
    getSummaryData: () => Promise<any>;
    getPrintData: () => Promise<ISummaryParams>;
    overlay: IFullscreenViewOverlay;
    setOverlay: Dispatch<SetStateAction<IFullscreenViewOverlay>>;
    options: ISelectOptions;
    setOptions: Dispatch<SetStateAction<ISelectOptions>>;
    page: PageId;
    setPage: Dispatch<SetStateAction<PageId>>;
    onFormBack: () => void;
    onFormProceed: () => void;
    onFormUpdated: () => void;
    onValidateForm: (pageId: PageId) => void;
    proceedClicked: boolean;
    setProceedClicked: Dispatch<SetStateAction<boolean>>;
    drawingMenuVisible: boolean;
    toggleDrawingMenuVisible: () => void;
    drawingInfoVisible: boolean;
    setDrawingInfoVisible: Dispatch<SetStateAction<boolean>>;
    conditionAgreed: boolean;
    setConditionAgreed: Dispatch<SetStateAction<boolean>>;
    drawnFeatures: Feature[];
    setDrawnFeatures: Dispatch<SetStateAction<Feature[]>>;
    restartApp: () => void;
}

interface ISelectOptions {
    [fieldName: string]: IEsriCodedValue[];
}

const RegistrationContext = createContext<IRegistrationContext>(null);

export const useRegistrationContext = (): IRegistrationContext => useContext(RegistrationContext);

export const RegistrationContextProvider: FC = (props: React.PropsWithChildren<IRegistrationContext>) => {
    const [userConsent, setUserConsent] = useState<boolean>(false);
    const [conditionAgreed, setConditionAgreed] = useState<boolean>(false);
    const [validateForm, setValidateForm] = useState<string>("1");
    const [resetForm, setResetForm1] = useState<number>(0);
    const [rawData, setRawData] = useState({});
    const [formFeature, setFormFeature] = useState<Feature>(null);
    const [attachments, setAttachments] = useState<FileList>(null);
    const [attachedProcurations, setAttachedProcurations] = useState<FileList>(null);
    const [summaryData, setSummaryData] = useState<ISummaryParams>();
    const [overlay, setOverlay] = useState<IFullscreenViewOverlay>(null);
    const [options, setOptions] = useState<ISelectOptions>({});
    const [page, setPage] = useState<PageId>(PageId.ApplicantForm);
    // const [page, setPage] = useState<PageId>(PageId.DrawingForm);
    const [proceedClicked, setProceedClicked] = useState<boolean>(false);
    const [drawingMenuVisible, setDrawingMenuVisible] = useState<boolean>(true);
    const [drawingInfoVisible, setDrawingInfoVisible] = useState<boolean>(false);
    const [drawnFeatures, setDrawnFeatures] = useState<Feature[]>([]);

    const drawingMenuVisibleRef = useRef<boolean>(true);

    const { basemap, getFieldValue, getFieldAlias, getDateFormated, fieldInfos, setHasMap } = useAppContext();

    useEffect(() => {
        if (fieldInfos) {
            const updatedOptions = {};
            config.forms.companyApplicantForm.fields.map((inputField) => {
                const field = inputField.field && fieldInfos.find((info) => info.name === inputField.field);
                field && field.domain && field.domain.codedValues
                    ? (updatedOptions[inputField.field] = field.domain.codedValues)
                    : false;
            });
            config.forms.termForm.fields.map((inputField) => {
                const field = inputField.field && fieldInfos.find((info) => info.name === inputField.field);
                field && field.domain && field.domain.codedValues
                    ? (updatedOptions[inputField.field] = field.domain.codedValues)
                    : false;
            });
            setOptions(updatedOptions);
        }
    }, [fieldInfos]);

    const prepareToResetForm = () => {
        setRawData({});
        setFormFeature(null);
        setValidateForm("1");
        setAttachments(null);
        setAttachedProcurations(null);
        setResetForm1(Date.now());
        setSummaryData(null);
        setDrawnFeatures([]);
        resetMinMaxDates();
    };
    const restartApp = () => {
        setUserConsent(false);
        setConditionAgreed(false);
        setValidateForm("1");
        setResetForm1(0);
        setRawData({});
        setFormFeature(null);
        setAttachments(null);
        setAttachedProcurations(null);
        setSummaryData(null);
        setOverlay(null);
        setPage(PageId.ApplicantForm);
        setProceedClicked(false);
        setDrawingMenuVisible(true);
        setDrawingInfoVisible(false);
        setDrawnFeatures([]);
        resetMinMaxDates();
    };

    const getSnapshot = useCallback(async () => {
        drawnFeatures && drawnFeatures.map((f) => f.setStyle(getDrawingSymbol(f.get("DRAWTYPE"), f.get("NR"))));
        return await generateSnapshot(drawnFeatures, basemap);
    }, [drawnFeatures]);

    const getOverview = useCallback(async () => {
        drawnFeatures && drawnFeatures.map((f) => f.setStyle(getOverviewMapSymbol(f.get("DRAWTYPE"))));
        return await generateOverview(drawnFeatures, basemap);
    }, [drawnFeatures]);

    const getSummaryData = async (): Promise<ISummaryParams> => {
        const attachedFiles = [];
        attachedProcurations && attachedFiles.push(...Array.from(attachedProcurations, (a) => a.name));
        attachments && attachedFiles.push(...Array.from(attachments, (a) => a.name));

        const attachedFileTitles = attachedFiles.length
            ? {
                  title: "Pielikumi:",
                  rows: attachedFiles
              }
            : undefined;

        return {
            paragraphs: getParagraphs(formFeature, drawnFeatures),
            attachments: attachedFileTitles,
            snapshot: await getSnapshot()
        };
    };

    const getPrintData = async (): Promise<ISummaryParams> => {
        const attachedFiles = [];
        attachedProcurations && attachedFiles.push(...Array.from(attachedProcurations, (a) => a.name));
        attachments && attachedFiles.push(...Array.from(attachments, (a) => a.name));

        return {
            paragraphs: getParagraphs(formFeature, drawnFeatures, attachedFiles),
            overview: await getOverview(),
            snapshot: await getSnapshot(),
            globalId: "Id: " + getFieldValue(formFeature, "GLOBALID"),
            date: "Datums: " + getDateFormated(Date.now())
        };
    };

    const onFormBack = useCallback(() => {
        if (page === PageId.TermForm) {
            setUserConsent(false);
            setOverlay(null);
            setValidateForm("1");
            setPage(PageId.ApplicantForm);
        } else if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setValidateForm("1");
            setPage(PageId.TermForm);
        } else if (page === PageId.SummaryForm) {
            setOverlay(null);
            setValidateForm("1");
            setConditionAgreed(false);
            setPage(PageId.DrawingForm);
        }
    }, [page]);

    const onFormProceed = useCallback(() => {
        if (page === PageId.Submit) {
            restartApp();
            return;
        }
        setProceedClicked(true);
        onValidateForm(page);
        if (page === PageId.SummaryForm) {
            onFormUpdated();
        }
    }, [page]);

    const onFormUpdated = useCallback(() => {
        if (page === PageId.ApplicantForm) {
            setPage(PageId.TermForm);
        } else if (page === PageId.TermForm) {
            setPage(PageId.DrawingForm);
            setHasMap(true);
        } else if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setPage(PageId.SummaryForm);
        } else if (page === PageId.SummaryForm) {
            setPage(PageId.Submit);
        }
    }, [page]);

    const onValidateForm = (id: PageId) => {
        setValidateForm(id + "-" + Date.now());
    };

    const toggleDrawingMenuVisible = () => {
        drawingMenuVisibleRef.current = !drawingMenuVisibleRef.current;
        setDrawingMenuVisible(drawingMenuVisibleRef.current);
    };

    const values = {
        userConsent,
        setUserConsent,
        validateForm,
        setValidateForm,
        resetForm,
        setResetForm: prepareToResetForm,
        rawData,
        setRawData,
        formFeature,
        setFormFeature,
        attachments,
        setAttachments,
        attachedProcurations,
        setAttachedProcurations,
        summaryData,
        setSummaryData,
        getSummaryData,
        getPrintData,
        overlay,
        setOverlay,
        options,
        setOptions,
        page,
        setPage,
        onFormBack,
        onFormProceed,
        onFormUpdated,
        onValidateForm,
        proceedClicked,
        setProceedClicked,
        drawingMenuVisible,
        toggleDrawingMenuVisible,
        drawingInfoVisible,
        setDrawingInfoVisible,
        conditionAgreed,
        setConditionAgreed,
        drawnFeatures,
        setDrawnFeatures,
        restartApp
    };

    return <RegistrationContext.Provider value={values}>{props.children}</RegistrationContext.Provider>;
};

const getParagraphs = (formFeature, drawnFeatures, attachedFiles?) => {
    const rows = [
        {
            title: "Izpildītājs:",
            rows: [
                formFeature.get("APPLICANTNAME"),
                formFeature.get("APPLICANTCODE") ? "Vienotais reģ. Nr.: " + formFeature.get("APPLICANTCODE") : null,
                "Adrese: " + formFeature.get("APPLICANTADDRESS"),
                "Telefons: " + formFeature.get("APPLICANTPHONE"),
                "E-pasts: " + formFeature.get("APPLICANTEMAIL")
            ]
        },
        {
            title: "Dati par plānotajiem darbiem:",
            rows: [
                formFeature.get("OBJECTNAME"),
                "Darbības termiņš no/līdz: " +
                    formatDate(formFeature.get("DATEFROM")) +
                    " - " +
                    formatDate(formFeature.get("DATETO"))
            ]
        },
        getPlaceSection(formFeature, drawnFeatures),
        {
            title: "Atbildīgais darbu vadītājs:",
            rows: [
                formFeature.get("RESPONSIBLEPERSON"),
                "Telefons: " + formFeature.get("RESPONSIBLEPHONE"),
                "E-pasts: " + formFeature.get("RESPONSIBLEMAIL")
            ]
        }
    ];
    attachedFiles &&
        rows.push({
            title: "Pielikumā:",
            rows: attachedFiles
        });

    const otherPerson =
        formFeature.get("APPLICANTTYPE") === ApplicantType.Company
            ? {
                  title: "Paraksttiesīgā vai pilnvarotā persona:",
                  rows: [
                      formFeature.get("AUTHORIZEDPERSON"),
                      "Ieņemamais amats: " + (formFeature.get("POSITION") || ""),
                      "Telefons: " + (formFeature.get("AUTHORIZEDPHONE") || ""),
                      "E-pasts: " + (formFeature.get("AUTHORIZEDMAIL") || "")
                  ]
              }
            : formFeature.get("OTHERCONTACTPERSON")
            ? {
                  title: "Cita kontaktpersona:",
                  rows: [formFeature.get("CONTACTPERSON"), "Telefons: " + (formFeature.get("CONTACTPERSONPHONE") || "")]
              }
            : null;
    otherPerson && rows.splice(1, 0, otherPerson);

    return rows;
};

const getPlaceSection = (formFeature: Feature, drawnFeatures: Feature[]) => {
    const points = drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.Point);
    const lines = drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.Polyline);
    const rows = [];

    points.length &&
        rows.push(
            "Rakšanas un šķērsošanas vieta: " + points.length + (points.length === 1 ? " objekts" : " objekti"),
            ...points.map((point) => point.get("NR") + ". - " + (point.get("DESCRIPTION") || "bez nosaukuma"))
        );

    lines.length &&
        rows.push(
            "Rakšanas un šķērsošanas darbu zona: " + lines.length + (lines.length === 1 ? " objekts" : " objekti"),
            ...lines.map((line) => line.get("NR") + ". - " + (line.get("DESCRIPTION") || "bez nosaukuma"))
        );

    rows.push(
        " ",
        "Tehnisko noteikumu numurs: " + (formFeature.get("TECHNICALNUMBER") || "-"),
        "Tehnisko noteikumu datums: " + (formatDate(formFeature.get("TECHNICALDATE")) || "-"),
        "Projekta saskaņošanas numurs: " + (formFeature.get("PROJECTNUMBER") || "-"),
        "Projekta saskaņošanas datums: " + (formatDate(formFeature.get("PROJECTDATE")) || "-")
    );

    return { title: "Plānoto darbu vieta", rows };
};

const formatDate = (date: string): string => date && date.split("-").reverse().join(".");

const resetMinMaxDates = () =>
    config.forms.termForm.fields.map((f) => {
        f.minDate && (f.minDate = undefined);
        f.maxDate && (f.maxDate = undefined);
    });
